import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Alert, Box, Button, Grid, Link } from '@mui/material'
import { t } from '@lingui/macro'
import ProgressBackdrop from '../utils/controls/ProgressBackdrop'
import AccountDataService from '../DataServices/AccountDataService'
import FormTextField from '../utils/controls/FormTextField'

type ForgotPasswordFormProps = {
  onFormSubmitted: () => void
  submitButtonText: string
}

type FormForgotPasswordFields = {
  email: string
}

export default function ForgotPasswordForm({
  onFormSubmitted,
  submitButtonText,
}: ForgotPasswordFormProps): JSX.Element {
  console.log('Load Forgot Password Form Component')

  const [submitSuccess, setSubmitSuccess] = useState<string>('')
  const [submitWarning, setSubmitWarning] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)

  const formSchema = yup.object().shape({
    email: yup
      .string()
      .label(t`The email address`)
      .required()
      .email(),
  })

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormForgotPasswordFields>({
    resolver: yupResolver(formSchema),
  })

  const handleFormSubmit = (data: FormForgotPasswordFields): void => {
    setSubmitSuccess('')
    setSubmitWarning('')
    setSubmitError('')
    setSubmitting(true)

    AccountDataService.sendPasswordResetEmail(data.email)
      .then(() => {
        setSubmitSuccess(
          t`We have sent an email to you for password changing. Follow the link provided to finalize the sign up process.`,
        )
      })
      .catch((err) => {
        console.log(
          'ERROR: An error occurred while resending verification email',
          err,
          err.response,
        )
        if (err.response.status === 400 && err.response.data === 'email_not_exists') {
          setSubmitWarning(t`This email not exists, please choose another one.`)
        } else {
          setSubmitError(t`An error occurred while resending verification email, please try again.`)
        }
      })
      .finally(() => {
        setSubmitting(false)
        onFormSubmitted()
      })
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{ mt: 2, width: '100%' }}
    >
      <FormTextField
        autoComplete='off'
        errorText={errors.email?.message}
        label={t`Email Address`}
        margin="normal"
        registerReturn={register('email')}
      />
      {submitSuccess !== '' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {submitSuccess}
        </Alert>
      )}
      {submitWarning !== '' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {submitWarning}
        </Alert>
      )}
      {submitError !== '' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}
      <Button color="primary" fullWidth sx={{ mb: 2, mt: 3 }} type="submit" variant="contained">
        {submitButtonText}
      </Button>
      <Grid container justifyContent="flex-end">
        <Grid item>
          <Link href="/auth/login/" variant="body2">
            {t`Sign In`}
          </Link>
        </Grid>
      </Grid>
      <ProgressBackdrop open={submitting} />
    </Box>
  )
}
